import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ChevronRight, Download } from 'lucide-react';
import { attendanceAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox, StackedBarChart, fmtCount, type BarSegment } from './DashboardPrimitives';
import { BlockLoader, Spinner } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import DrillLink from './DrillLink';
import { DRILL_ROUTES, buildDrillUrl, absentRunRange, absentMonthRange } from '../../utils/drilldown';
import { useDashboardList } from './useDashboardList';
import { ATTENDANCE_LIST, readAbsentCounts } from './lists/attendance';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror the dashboard-attendance.controller responses)

interface TrendDay {
  date: string;
  label: number;
  present: number;
  work_from_home: number;
  half_day: number;
  on_leave: number;
  absent: number;
  week_off: number;
  holiday: number;
  day_in_progress: number;
  not_marked: number;
  future: number;
  leave_breakdown: Record<string, number>;
  total: number;
}

interface TrendData {
  month: { ym: string; label: string; startDate: string; endDate: string; daysInMonth: number; isCurrentMonth: boolean };
  employeeCount: number;
  activeEmployeeCount: number;
  daily: TrendDay[];
  averages: { attendancePct: number | null; presentPct: number | null };
}

interface AbsentRow {
  emp_code: string;
  employee: string;
  mobile: string;
  store: string;
  city: string;
  missing_days?: number;
  status?: 'absent' | 'not_marked';
}

interface AbsenteesData {
  today: string;
  continuous: { total: number; rows: AbsentRow[] };
  absentToday: { total: number; rows: AbsentRow[] };
}

interface TrendDayRow {
  emp_code: string;
  employee: string;
  store: string;
  city: string;
  statusLabel: string;
  in_time: string;
  out_time: string;
}

// Employee Attendance Map — per-employee present/absent day counts for the month.
interface AttendanceMapRow {
  emp_code: string;
  employee: string;
  mobile: string;
  store: string;
  city: string;
  present_days: number;
  absent_days: number;
}

interface AttendanceMapData {
  total: number;
  absent_over_3: number;
  rows: AttendanceMapRow[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Status colours (match the Project-Management reference taxonomy)

const LEAVE_HEX: Record<string, string> = {
  'Earned Leave': '#f59e0b',
  'Casual Leave': '#8b5cf6',
  'Sick Leave': '#f43f5e',
  'Maternity Leave': '#ec4899',
  'Paternity Leave': '#c026d3',
  'Extra-ordinary Leave': '#f97316',
  'Restricted Holiday': '#65a30d',
  'Comp Off': '#0891b2',
  'Extended ML': '#9333ea',
  Other: '#94a3b8',
};
const LEAVE_ORDER = Object.keys(LEAVE_HEX);
const EXTRA_PALETTE = [
  '#0ea5e9', '#a855f7', '#d946ef', '#84cc16', '#14b8a6', '#eab308',
  '#fb7185', '#22d3ee', '#f472b6', '#34d399', '#fbbf24', '#60a5fa',
];
const leaveHex = (type: string): string => {
  if (LEAVE_HEX[type]) return LEAVE_HEX[type];
  let h = 0;
  for (let i = 0; i < type.length; i += 1) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return EXTRA_PALETTE[h % EXTRA_PALETTE.length];
};

type Segment = BarSegment;

// Fixed (non-leave) segments. Leave-type segments are inserted between
// work_from_home and absent at render time, driven by the month's data.
const HEAD_SEGMENTS: Segment[] = [
  { key: 'present', label: 'Present', hex: '#22c55e' },
  { key: 'work_from_home', label: 'Work from Home', hex: '#10b981' },
  { key: 'half_day', label: 'Half Day', hex: '#eab308' },
];
const TAIL_SEGMENTS: Segment[] = [
  { key: 'absent', label: 'Absent', hex: '#ef4444' },
  { key: 'week_off', label: 'Week Off', hex: '#6366f1' },
  { key: 'holiday', label: 'Holiday', hex: '#3b82f6' },
  { key: 'day_in_progress', label: 'Day in Progress', hex: '#14b8a6' },
  { key: 'not_marked', label: 'Not Marked', hex: '#d1d5db' },
  { key: 'future', label: 'Future', hex: '#e5e7eb' },
];
// Hidden until the user toggles them on in the legend (avoids burying real
// statuses under a wall of future days).
const DEFAULT_HIDDEN = new Set(['future']);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

const fmtDays = (n: number): string => `${Number(n.toFixed(2))} day${Number(n.toFixed(2)) === 1 ? '' : 's'}`;

const getSegmentCount = (day: TrendDay, key: string): number => {
  if (key.startsWith('leave:')) return day.leave_breakdown?.[key.slice(6)] || 0;
  return (day as unknown as Record<string, number>)[key] || 0;
};

// ─────────────────────────────────────────────────────────────────────────────

export default function AttendanceTab({ projectId, month, roleId, filters }: { projectId: number | null; month: string; roleId?: string; filters?: DashRichFilterParams }) {
  const monthYm = month;

  const [trend, setTrend] = useState<TrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [absentees, setAbsentees] = useState<AbsenteesData | null>(null);
  const [absLoading, setAbsLoading] = useState(false);
  const [absError, setAbsError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drill-down (clicked bar segment)
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillDate, setDrillDate] = useState('');
  const [drillLabel, setDrillLabel] = useState('');
  const [drillRows, setDrillRows] = useState<TrendDayRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  // The two absent lists open by address (?list=): a card's number and "View All" both
  // write it, and what is open is read from it on every render. Held in state it would
  // be shut by the reload that follows the default Role filter.
  const { list, openList, closeList } = useDashboardList();
  const [mapOpen, setMapOpen] = useState(false);

  // Employee Attendance Map (month-scoped)
  const [mapData, setMapData] = useState<AttendanceMapData | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const trendReq = useRef(0);
  const absReq = useRef(0);
  const drillReq = useRef(0);
  const mapReq = useRef(0);

  // ── Fetch trend on month / project change ──
  useEffect(() => {
    const reqId = ++trendReq.current;
    setTrendLoading(true);
    setTrendError(null);
    attendanceAPI
      .getDashboardTrend({ month: monthYm, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== trendReq.current) return;
        setTrend((res.data?.data ?? null) as TrendData | null);
      })
      .catch((err) => {
        if (reqId !== trendReq.current) return;
        setTrendError(err.response?.data?.message || 'Failed to load attendance trend');
        setTrend(null);
      })
      .finally(() => {
        if (reqId === trendReq.current) setTrendLoading(false);
      });
  }, [monthYm, projectId, roleId, filters]);

  // ── Fetch absentees (today-anchored) on project / role change ──
  useEffect(() => {
    const reqId = ++absReq.current;
    setAbsLoading(true);
    setAbsError(null);
    attendanceAPI
      .getDashboardAbsentees({ projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== absReq.current) return;
        setAbsentees(res.data.data as AbsenteesData);
      })
      .catch((err) => {
        if (reqId !== absReq.current) return;
        setAbsError(err.response?.data?.message || 'Failed to load absentees');
        setAbsentees(null);
      })
      .finally(() => {
        if (reqId === absReq.current) setAbsLoading(false);
      });
  }, [projectId, roleId, filters]);

  // ── Fetch attendance map (month-scoped) on month / project / role change ──
  useEffect(() => {
    const reqId = ++mapReq.current;
    setMapLoading(true);
    setMapError(null);
    attendanceAPI
      .getDashboardAttendanceMap({ month: monthYm, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== mapReq.current) return;
        setMapData((res.data?.data ?? null) as AttendanceMapData | null);
      })
      .catch((err) => {
        if (reqId !== mapReq.current) return;
        setMapError(err.response?.data?.message || 'Failed to load attendance map');
        setMapData(null);
      })
      .finally(() => {
        if (reqId === mapReq.current) setMapLoading(false);
      });
  }, [monthYm, projectId, roleId, filters]);

  // ── The absent lists: only from an absentees answer that has arrived ──
  // While the call is on its way, or after it failed, nothing is counted and no list is
  // shown: a number that is not known is never drawn as zero.
  const absentLists = !absLoading && !absError ? absentees : null;
  const absentCounts = useMemo(() => readAbsentCounts(absentLists), [absentLists]);

  // ── Derived: ordered segments incl. the month's leave types ──
  const segments = useMemo<Segment[]>(() => {
    const leaveTypes = new Set<string>();
    trend?.daily.forEach((d) => Object.keys(d.leave_breakdown || {}).forEach((t) => leaveTypes.add(t)));
    const ordered = Array.from(leaveTypes).sort((a, b) => {
      const ia = LEAVE_ORDER.indexOf(a);
      const ib = LEAVE_ORDER.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
    const leaveSegments: Segment[] = ordered.map((t) => ({ key: `leave:${t}`, label: t, hex: leaveHex(t) }));
    return [...HEAD_SEGMENTS, ...leaveSegments, ...TAIL_SEGMENTS];
  }, [trend]);

  const visibleKeys = useMemo<Set<string>>(() => {
    if (selected.size > 0) return new Set(selected);
    return new Set(segments.map((s) => s.key).filter((k) => !DEFAULT_HIDDEN.has(k)));
  }, [selected, segments]);

  const toggleSegment = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Drill-down ──
  const openDrill = useCallback(
    (date: string, statusKey: string, label: string) => {
      setDrillOpen(true);
      setDrillDate(date);
      setDrillLabel(label);
      setDrillRows([]);
      setDrillSearch('');
      setDrillLoading(true);
      const reqId = ++drillReq.current;
      attendanceAPI
        .getDashboardTrendDay({ date, status: statusKey, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
        .then((res) => {
          if (reqId !== drillReq.current) return;
          setDrillRows((res.data.data?.rows as TrendDayRow[]) || []);
        })
        .catch(() => {
          if (reqId === drillReq.current) setDrillRows([]);
        })
        .finally(() => {
          if (reqId === drillReq.current) setDrillLoading(false);
        });
    },
    [projectId, roleId, filters],
  );

  const filteredDrillRows = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    if (!q) return drillRows;
    return drillRows.filter((r) =>
      [r.employee, r.emp_code, r.store, r.city].some((v) => (v || '').toLowerCase().includes(q)),
    );
  }, [drillRows, drillSearch]);

  const drillSort = useTableSort(filteredDrillRows, {
    employee: (r) => r.employee,
    store: (r) => r.store,
    city: (r) => r.city,
    status: (r) => r.statusLabel,
    in_time: (r) => r.in_time,
    out_time: (r) => r.out_time,
  });

  // ── CSV download (day-wise summary) ──
  const handleDownload = () => {
    if (!trend) return;
    const leaveTypes = segments.filter((s) => s.key.startsWith('leave:')).map((s) => s.label);
    const headers = [
      'Date', 'Present', 'Work from Home', 'Half Day', ...leaveTypes,
      'Absent', 'Week Off', 'Holiday', 'Day in Progress', 'Not Marked', 'Future', 'Total',
    ];
    const rows = trend.daily.map((d) => [
      d.date,
      fmtCount(d.present),
      fmtCount(d.work_from_home),
      fmtCount(d.half_day),
      ...leaveTypes.map((t) => fmtCount(d.leave_breakdown?.[t] || 0)),
      fmtCount(d.absent),
      fmtCount(d.week_off),
      fmtCount(d.holiday),
      fmtCount(d.day_in_progress),
      fmtCount(d.not_marked),
      fmtCount(d.future),
      fmtCount(d.total),
    ]);
    downloadCsv(`Attendance_Trend_${monthYm}.csv`, buildCsv(headers, rows));
  };

  // ── CSV download (Employee Attendance Map — full list) ──
  const handleMapDownload = () => {
    const rows = mapData?.rows ?? [];
    if (rows.length === 0) return;
    const headers = ['Employee Code', 'Employee Name', 'Store', 'City', 'Mobile Number', 'Present Days', 'Absent Days'];
    const data = rows.map((r) => [
      r.emp_code,
      r.employee,
      r.store || '-',
      r.city || '-',
      r.mobile || '-',
      String(r.present_days),
      String(r.absent_days),
    ]);
    downloadCsv(`Employee_Attendance_Map_${monthYm}.csv`, buildCsv(headers, data));
  };

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* ─── Module 1: Attendance Trend ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Attendance Trend</h2>
            <p className="text-xs text-gray-500">Date-wise status distribution for selected month</p>
          </div>
          <div className="flex items-center justify-center gap-6 text-center">
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Month's Average Attendance</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">
                {trend?.averages.attendancePct == null ? '—' : `${trend.averages.attendancePct}%`}
              </span>
            </div>
            <div className="h-8 w-px bg-gray-200" aria-hidden="true" />
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Month's Average Present</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">
                {trend?.averages.presentPct == null ? '—' : `${trend.averages.presentPct}%`}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-start gap-2 md:justify-end">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!trend || trendLoading}
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <span className="font-medium">Employees in scope: {trend?.employeeCount ?? '—'}</span>
          <span className="font-medium" title="On-roll right now — joined, not yet exited, currently active. Comparable to Project Management's 'Employees' figure. Not the denominator of the % averages.">Active today: {trend?.activeEmployeeCount ?? '—'}</span>
          {trend && <span>Range: {trend.month.startDate} to {trend.month.endDate}</span>}
        </div>

        {/* Chart */}
        <div className="mt-4">
          {trendLoading ? (
            <BlockLoader className="h-72" />
          ) : trendError ? (
            <div className="flex h-72 items-center justify-center text-sm text-rose-600">{trendError}</div>
          ) : !trend || trend.employeeCount === 0 ? (
            <div className="flex h-72 items-center justify-center text-sm text-gray-500">No attendance data for this month.</div>
          ) : (
            <StackedBarChart
              days={trend.daily}
              segments={segments}
              visibleKeys={visibleKeys}
              getCount={(day, key) => getSegmentCount(day, key)}
              getDayTotal={(day) => day.total}
              onSegmentClick={(day, seg) => openDrill(day.date, seg.key, seg.label)}
              tooltipLines={(_day, _seg, count, dayTotal) => [
                `Count: ${fmtCount(count)}`,
                `Share: ${dayTotal > 0 ? Number(((count / dayTotal) * 100).toFixed(1)) : 0}%`,
              ]}
            />
          )}
        </div>

        {/* Legend (multi-select filter) */}
        {trend && trend.employeeCount > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {segments.map((s) => {
              const isSelected = selected.has(s.key);
              const dimmed = selected.size > 0 && !isSelected;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleSegment(s.key)}
                  className={`inline-flex items-center gap-1.5 text-xs ${
                    dimmed ? 'text-gray-400 opacity-60' : isSelected ? 'font-semibold text-gray-900' : 'text-gray-600'
                  }`}
                >
                  <span
                    className={`h-3 w-3 rounded-sm ${isSelected ? 'ring-2 ring-gray-500 ring-offset-1' : ''}`}
                    style={{ backgroundColor: s.hex }}
                  />
                  {s.label}
                </button>
              );
            })}
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-xs font-medium text-primary-600 hover:underline">
                Clear all
              </button>
            )}
          </div>
        )}
      </section>

      {/* ─── Module 2: Continuously Absent + Absent Today ─── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <AbsentCard
          title="Continuously Absent"
          subtitle="Current run of 2+ absent days, ending today"
          loading={absLoading}
          error={absError}
          rows={absentees?.continuous.rows ?? []}
          total={absentees?.continuous.total ?? 0}
          count={absentCounts.continuous}
          variant="continuous"
          todayIsoStr={absentees?.today}
          onViewAll={() => openList(ATTENDANCE_LIST.continuous)}
        />
        <AbsentCard
          title="Absent Today"
          subtitle="Shift start passed with no check-in — no leave applied"
          loading={absLoading}
          error={absError}
          rows={absentees?.absentToday.rows ?? []}
          total={absentees?.absentToday.total ?? 0}
          count={absentCounts.absentToday}
          variant="today"
          todayIsoStr={absentees?.today}
          onViewAll={() => openList(ATTENDANCE_LIST.absentToday)}
        />
      </div>

      {/* ─── Module 3: Employee Attendance Map (month-scoped) ─── */}
      <AttendanceMapCard
        monthYm={monthYm}
        todayIsoStr={absentees?.today}
        subtitle={`${mapData?.absent_over_3 ?? 0} Employees Absent > 3 days`}
        loading={mapLoading}
        error={mapError}
        rows={mapData?.rows ?? []}
        total={mapData?.total ?? 0}
        onViewAll={() => setMapOpen(true)}
        onDownload={handleMapDownload}
      />

      {/* Drill-down modal */}
      {drillOpen &&
        createPortal(
          <Modal onClose={() => setDrillOpen(false)} title={`${drillLabel} — ${drillDate}`}>
            <div className="mb-3">
              <SearchBox value={drillSearch} onChange={setDrillSearch} placeholder="Search by name, code, store…" />
            </div>
            {drillLoading ? (
              <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading">
                <Spinner size="md" />
              </div>
            ) : filteredDrillRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">No employees.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                      <SortTh label="Employee" sortKey="employee" sort={drillSort} className="pb-3 font-semibold" />
                      <SortTh label="Store" sortKey="store" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="City" sortKey="city" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Status" sortKey="status" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Sign In" sortKey="in_time" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Sign Out" sortKey="out_time" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {drillSort.sorted.map((r) => (
                      <tr key={r.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-3 font-medium text-gray-900">
                          {r.employee}
                          <span className="ml-1 text-xs text-gray-400">({r.emp_code})</span>
                        </td>
                        <td className="py-3 pl-4 text-gray-700">{r.store || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.city || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.statusLabel}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.in_time || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.out_time || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>,
          document.body,
        )}

      {/* View-all modals. The two absent lists are open while the address names them
          and their answer has arrived; closing takes the name out of the address. */}
      {list === ATTENDANCE_LIST.absentToday && absentLists &&
        createPortal(
          <AbsentModal
            title="Absent Today"
            rows={absentLists.absentToday.rows}
            variant="today"
            onClose={closeList}
          />,
          document.body,
        )}

      {list === ATTENDANCE_LIST.continuous && absentLists &&
        createPortal(
          <AbsentModal
            title="Continuously Absent"
            rows={absentLists.continuous.rows}
            variant="continuous"
            onClose={closeList}
          />,
          document.body,
        )}

      {mapOpen &&
        createPortal(
          <AttendanceMapModal
            rows={mapData?.rows ?? []}
            onClose={() => setMapOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A list card's headline number: how many people the full list holds, and it says
// "people". Above zero it is a button that opens that list (the chevron says so, as on a
// headline tile); at zero it is plain. The card does not draw it at all while the count
// is not known.

function ListCount({ name, count, onOpen }: { name: string; count: number; onOpen: () => void }): ReactElement {
  const value = count.toLocaleString('en-IN');
  const unit = count === 1 ? 'person' : 'people';
  const body = (
    <>
      <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900 transition group-hover:text-primary-600">{value}</span>
      <span className="text-xs font-medium text-gray-500">{unit}</span>
    </>
  );
  if (count <= 0) return <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">{body}</span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${name}: ${value} ${unit}. Open the full list`}
      className="group inline-flex items-baseline gap-1.5 whitespace-nowrap rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      {body}
      <ChevronRight className="h-3.5 w-3.5 self-center text-gray-300 transition group-hover:text-primary-500" aria-hidden />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card (top 10) for Continuously Absent / Absent Today

function AbsentCard({
  title,
  subtitle,
  loading,
  error,
  rows,
  total,
  count,
  variant,
  onViewAll,
  todayIsoStr,
}: {
  title: string;
  subtitle: string;
  loading: boolean;
  error: string | null;
  rows: AbsentRow[];
  total: number;
  /** People on the full list, for the headline number; null while it is not known. */
  count: number | null;
  variant: 'continuous' | 'today';
  onViewAll: () => void;
  /** Server IST 'today' (absentees.today) — anchors the drill-down window. */
  todayIsoStr?: string;
}) {
  const top = rows.slice(0, 10);
  const sort = useTableSort(top, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    metric: (r) =>
      variant === 'continuous'
        ? r.missing_days ?? 0
        : r.status === 'not_marked'
          ? 'Not checked in'
          : 'Absent',
  });
  return (
    <section className="flex min-h-[430px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {/* The header carries the list's headline number too. The title gives way first;
          on a narrow screen the number and buttons drop under it. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-48">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {count !== null && <ListCount name={title} count={count} onOpen={onViewAll} />}
          {total > 0 && (
            <button
              type="button"
              onClick={onViewAll}
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
            >
              View All <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <BlockLoader className="flex-1 py-0" />
      ) : error ? (
        <div className="flex flex-1 items-center justify-center text-sm text-rose-600">{error}</div>
      ) : top.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
          {variant === 'continuous' ? 'No continuously absent employees.' : 'No one absent today.'}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
              <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
              <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              <SortTh
                label={variant === 'continuous' ? 'Days Absent' : 'Status'}
                sortKey="metric"
                sort={sort}
                align="right"
                className="min-w-[120px] whitespace-nowrap pb-3 pl-4 font-semibold"
              />
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((row) => (
              <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 font-medium text-gray-900">
                  {/* Drill-down: open Daily Attendance for THIS employee over the window
                      this row is about — the absent run for 'continuous', today for
                      'today' — so the dashboard number can be verified at source. */}
                  <DrillLink
                    href={buildDrillUrl(DRILL_ROUTES.dailyAttendance, {
                      employeeCode: row.emp_code,
                      ...(variant === 'continuous'
                        ? absentRunRange(row.missing_days ?? 1, todayIsoStr)
                        : { startDate: todayIsoStr, endDate: todayIsoStr }),
                    })}
                    title={`Open Daily Attendance for ${row.employee} (${row.emp_code})`}
                    className="font-medium"
                  >
                    {row.employee}
                  </DrillLink>
                  <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                  <div className="mt-0.5 text-xs font-normal text-gray-500">
                    {[row.store, row.city].filter(Boolean).join(' • ') || '—'}
                  </div>
                </td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                <td className="py-3 pl-4 text-right">
                  <span className="font-bold text-rose-500">
                    {variant === 'continuous'
                      ? fmtDays(row.missing_days ?? 0)
                      : row.status === 'not_marked'
                        ? 'Not checked in'
                        : 'Absent'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && total > 10 && (
        <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {total}</div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// View-all modal (search + optional days filter)

function AbsentModal({
  title,
  rows,
  variant,
  onClose,
}: {
  title: string;
  rows: AbsentRow[];
  variant: 'continuous' | 'today';
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [op, setOp] = useState<'gte' | 'lte' | 'eq'>('gte');
  const [daysInput, setDaysInput] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const threshold = daysInput.trim() === '' ? null : Number(daysInput);
    return rows.filter((r) => {
      const matchesSearch =
        !q || [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q));
      if (!matchesSearch) return false;
      if (variant === 'continuous' && threshold != null && !Number.isNaN(threshold)) {
        const d = r.missing_days ?? 0;
        if (op === 'gte') return d >= threshold;
        if (op === 'lte') return d <= threshold;
        return Math.abs(d - threshold) < 0.001;
      }
      return true;
    });
  }, [rows, search, op, daysInput, variant]);

  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    metric: (r) =>
      variant === 'continuous'
        ? r.missing_days ?? 0
        : r.status === 'not_marked'
          ? 'Not checked in'
          : 'Absent',
  });

  return (
    <Modal onClose={onClose} title={title}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="min-w-[220px] flex-1">
          <SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" />
        </div>
        {variant === 'continuous' && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Absent for</span>
            <select
              value={op}
              onChange={(e) => setOp(e.target.value as 'gte' | 'lte' | 'eq')}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
            >
              <option value="gte">≥ at least</option>
              <option value="lte">≤ at most</option>
              <option value="eq">= exactly</option>
            </select>
            <input
              type="number"
              min={0}
              value={daysInput}
              onChange={(e) => setDaysInput(e.target.value)}
              placeholder="days"
              className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
            />
          </div>
        )}
      </div>
      <div className="mb-2 text-xs text-gray-500">
        {filtered.length} of {rows.length} employees
      </div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh
                  label={variant === 'continuous' ? 'Days Absent' : 'Status'}
                  sortKey="metric"
                  sort={sort}
                  align="right"
                  className="min-w-[120px] whitespace-nowrap pb-3 pl-4 font-semibold"
                />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">
                      {[row.store, row.city].filter(Boolean).join(' • ') || '—'}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                  <td className="py-3 pl-4 text-right">
                    <span className="font-bold text-rose-500">
                      {variant === 'continuous'
                        ? fmtDays(row.missing_days ?? 0)
                        : row.status === 'not_marked'
                          ? 'Not checked in'
                          : 'Absent'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Employee Attendance Map — month-scoped per-employee absent-day counts.
// The card shows the 10 most-absent employees; "View All" opens the full,
// searchable / threshold-filterable list. Absent Days come from the same
// classifyDay taxonomy as the trend chart above (today, still in progress, is
// excluded), so the two modules stay internally consistent.

const fmtAbsentDays = (n: number): number => Number((n ?? 0).toFixed(2));

function AttendanceMapCard({
  subtitle,
  loading,
  error,
  rows,
  total,
  onViewAll,
  onDownload,
  monthYm,
  todayIsoStr,
}: {
  subtitle: string;
  loading: boolean;
  error: string | null;
  rows: AttendanceMapRow[];
  total: number;
  onViewAll: () => void;
  onDownload: () => void;
  /** Selected month 'YYYY-MM' + server IST today — anchor the drill-down window. */
  monthYm: string;
  todayIsoStr?: string;
}) {
  const top = rows.slice(0, 10);
  const sort = useTableSort(top, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    absent: (r) => r.absent_days,
  });
  return (
    <section className="flex min-h-[430px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Employee Attendance Map</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDownload}
            disabled={loading || rows.length === 0}
            className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
          {total > 0 && (
            <button
              type="button"
              onClick={onViewAll}
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
            >
              View All <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <BlockLoader className="flex-1 py-0" />
      ) : error ? (
        <div className="flex flex-1 items-center justify-center text-sm text-rose-600">{error}</div>
      ) : top.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">No attendance map data.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
              <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
              <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              <SortTh
                label="Absent Days"
                sortKey="absent"
                sort={sort}
                align="right"
                className="min-w-[120px] whitespace-nowrap pb-3 pl-4 font-semibold"
              />
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((row) => (
              <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 font-medium text-gray-900">
                  {/* Drill-down: Daily Attendance for this employee across the SELECTED
                      MONTH. Absences only accrue for days before today, so the current
                      month's window ends yesterday — matching what this count counted. */}
                  <DrillLink
                    href={buildDrillUrl(DRILL_ROUTES.dailyAttendance, {
                      employeeCode: row.emp_code,
                      ...absentMonthRange(monthYm, todayIsoStr),
                    })}
                    title={`Open Daily Attendance for ${row.employee} (${row.emp_code})`}
                    className="font-medium"
                  >
                    {row.employee}
                  </DrillLink>
                  <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                  <div className="mt-0.5 text-xs font-normal text-gray-500">
                    {[row.store, row.city].filter(Boolean).join(' • ') || '—'}
                  </div>
                </td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                <td className="py-3 pl-4 text-right">
                  <span className="font-bold text-rose-500">{fmtAbsentDays(row.absent_days)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && total > 10 && (
        <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {total}</div>
      )}
    </section>
  );
}

function AttendanceMapModal({
  rows,
  onClose,
}: {
  rows: AttendanceMapRow[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [op, setOp] = useState<'gte' | 'lte' | 'eq'>('gte');
  const [daysInput, setDaysInput] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const threshold = daysInput.trim() === '' ? null : Number(daysInput);
    return rows.filter((r) => {
      const matchesSearch =
        !q || [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q));
      if (!matchesSearch) return false;
      if (threshold != null && !Number.isNaN(threshold)) {
        const d = r.absent_days ?? 0;
        if (op === 'gte') return d >= threshold;
        if (op === 'lte') return d <= threshold;
        return Math.abs(d - threshold) < 0.001;
      }
      return true;
    });
  }, [rows, search, op, daysInput]);

  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    absent: (r) => r.absent_days,
  });

  return (
    <Modal onClose={onClose} title="Employee Attendance Map">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="min-w-[220px] flex-1">
          <SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Absent for</span>
          <select
            value={op}
            onChange={(e) => setOp(e.target.value as 'gte' | 'lte' | 'eq')}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
          >
            <option value="gte">≥ at least</option>
            <option value="lte">≤ at most</option>
            <option value="eq">= exactly</option>
          </select>
          <input
            type="number"
            min={0}
            value={daysInput}
            onChange={(e) => setDaysInput(e.target.value)}
            placeholder="days"
            className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
          />
        </div>
      </div>
      <div className="mb-2 text-xs text-gray-500">
        {filtered.length} of {rows.length} employees
      </div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh
                  label="Absent Days"
                  sortKey="absent"
                  sort={sort}
                  align="right"
                  className="min-w-[120px] whitespace-nowrap pb-3 pl-4 font-semibold"
                />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">
                      {[row.store, row.city].filter(Boolean).join(' • ') || '—'}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                  <td className="py-3 pl-4 text-right">
                    <span className="font-bold text-rose-500">{fmtAbsentDays(row.absent_days)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
